import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { fr } from './fr';

export type Language = 'en' | 'fr';

export const LANGUAGE_STORAGE_KEY = 'guardian_language';

const dictionaries: Record<Language, Record<string, string>> = {
  // English source strings are used as dictionary keys, so 'en' needs no entries.
  en: {},
  fr,
};

const locales: Record<Language, string> = {
  en: 'en-GB',
  fr: 'fr-FR',
};

export type TranslateVars = Record<string, string | number>;

function detectLanguage(): Language {
  try {
    const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored === 'en' || stored === 'fr') return stored;
    if ((navigator.language || '').toLowerCase().startsWith('fr')) return 'fr';
  } catch {
    // localStorage unavailable (tests, privacy mode) — fall through to default.
  }
  return 'en';
}

let currentLanguage: Language = detectLanguage();

function interpolate(template: string, vars?: TranslateVars): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (match, key) =>
    vars[key] !== undefined ? String(vars[key]) : match
  );
}

/**
 * A source string may carry a disambiguation suffix ("Open||alarm status")
 * when the same English word needs different translations per context. The
 * suffix is part of the dictionary key but never displayed.
 */
function stripContext(text: string): string {
  const separatorIndex = text.indexOf('||');
  return separatorIndex === -1 ? text : text.slice(0, separatorIndex);
}

function translate(language: Language, text: string, vars?: TranslateVars): string {
  const template =
    language === 'en' ? stripContext(text) : dictionaries[language][text] ?? stripContext(text);
  return interpolate(template, vars);
}

function translatePlural(
  language: Language,
  singular: string,
  plural: string,
  count: number,
  vars?: TranslateVars
): string {
  // French treats 0 as singular ("0 agent"); English does not ("0 guards").
  const useSingular = language === 'fr' ? Math.abs(count) < 2 : Math.abs(count) === 1;
  return translate(language, useSingular ? singular : plural, { count, ...vars });
}

/**
 * Module-level translator for non-component code (services, export helpers).
 * Components should prefer `useI18n()` so they re-render on language change.
 */
export function t(text: string, vars?: TranslateVars): string {
  return translate(currentLanguage, text, vars);
}

/** Plural-aware translator: `tn('{count} guard', '{count} guards', total)`. */
export function tn(singular: string, plural: string, count: number, vars?: TranslateVars): string {
  return translatePlural(currentLanguage, singular, plural, count, vars);
}

/** BCP 47 locale for Intl date/number formatting, e.g. 'fr-FR'. */
export function getLocale(): string {
  return locales[currentLanguage];
}

export function getLanguage(): Language {
  return currentLanguage;
}

interface I18nContextValue {
  language: Language;
  locale: string;
  setLanguage: (language: Language) => void;
  t: (text: string, vars?: TranslateVars) => string;
  tn: (singular: string, plural: string, count: number, vars?: TranslateVars) => string;
}

const I18nContext = createContext<I18nContextValue | undefined>(undefined);

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>(currentLanguage);

  const setLanguage = useCallback((next: Language) => {
    currentLanguage = next;
    try {
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, next);
    } catch {
      // Persisting the preference is best-effort.
    }
    document.documentElement.lang = next;
    setLanguageState(next);
  }, []);

  const value = useMemo<I18nContextValue>(
    () => ({
      language,
      locale: locales[language],
      setLanguage,
      t: (text, vars) => translate(language, text, vars),
      tn: (singular, plural, count, vars) => translatePlural(language, singular, plural, count, vars),
    }),
    [language, setLanguage]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
}
